'use client';

import { useEffect, useState } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';

import { ESessionStorageKeys } from '@/lib/enums';
import { PortalBootResponse } from '@/lib/types';
import { webStorageUtil } from '@/state/webStorage.state.service';

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
}

const useSaveToHomePage = (publisherMetaData: PortalBootResponse) => {
  const [isSaveToHomePageOpen, setIsSaveToHomePageOpen] = useState(false);
  const [isDisplayAddToHomePageButton, setIsDisplayAddToHomePageButton] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [isAndroidFallBack, setIsAndroidFallBack] = useState(false);

  const shouldDisplayButton = () => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isStandaloneUrl = window.location.search.includes('mode=standalone');
    const sourceIsHomeScreen = webStorageUtil.get(ESessionStorageKeys.SOURCE) === 'home screen bookmark';
    const hasInstallPromptFired = webStorageUtil.get(ESessionStorageKeys.INSTALL_PROMPT_FIRED);

    if (isStandalone || isStandaloneUrl) return false;

    return !!(
      publisherMetaData.settings?.addToHomeScreen?.active &&
      (isIOS || isAndroid) &&
      !sourceIsHomeScreen &&
      (hasInstallPromptFired || !sourceIsHomeScreen)
    );
  };

  const updateButtonDisplay = () => {
    const shouldDisplay = shouldDisplayButton();
    setIsDisplayAddToHomePageButton(shouldDisplay);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as InstallPromptEvent);
      webStorageUtil.set(ESessionStorageKeys.INSTALL_PROMPT_FIRED, 'true');
      updateButtonDisplay();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleAppInstalled = () => {
      setIsDisplayAddToHomePageButton(false);
      webStorageUtil.set(ESessionStorageKeys.SOURCE, 'home screen bookmark');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, []);

  // Read outside the dependency array: that array is evaluated during render,
  // which the server does too.
  const locationSearch = typeof window === 'undefined' ? '' : window.location.search;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isStandaloneUrl = window.location.search.includes('mode=standalone');

    if (isStandalone || isStandaloneUrl) {
      setIsDisplayAddToHomePageButton(false);
      setIsSaveToHomePageOpen(false);
      webStorageUtil.set(ESessionStorageKeys.SOURCE, 'home screen bookmark');
    } else {
      webStorageUtil.remove(ESessionStorageKeys.SOURCE);
    }

    updateButtonDisplay();
  }, [locationSearch]);

  useEffect(() => {
    updateButtonDisplay();
  }, [publisherMetaData.settings?.addToHomeScreen?.active]);


  const handleInstallApp = async () => {
    if (installPrompt) {
      try {
        await installPrompt.prompt();
        setInstallPrompt(null);
        webStorageUtil.remove(ESessionStorageKeys.INSTALL_PROMPT_FIRED);
        updateButtonDisplay();
      } catch (error) {
        console.error('Install prompt failed', error);
      }
    } else {
      setIsAndroidFallBack(true);
      console.log('Install prompt not available');
    }
  };

  const handleCloseSaveToHomePage = () => {
    setIsSaveToHomePageOpen(false);
    setIsAndroidFallBack(false);
  };

  return {
    isSaveToHomePageOpen,
    setIsSaveToHomePageOpen,
    isDisplayAddToHomePageButton,
    handleCloseSaveToHomePage,
    handleInstallApp,
    isAndroidFallBack
  };
};

export default useSaveToHomePage;
